import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { AlertCircle, Check, CircleAlert, Inbox, MessageSquare, Receipt, X } from 'lucide-react'
import { toast } from 'sonner'
import { LoadingButton } from '@/components/loading-button'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useApproveMessage, useRejectMessage, useSendMessage, useUpdateMessage } from '@/features/communications/hooks'
import { POConversations } from '@/features/communications/po-conversations'
import { actionTypeBadge } from '@/features/purchase-orders/action-audit-model'
import { formatApiErrorMessage } from '@/lib/api-error'
import { useReceivablesApi } from '@/features/receivables/api'
import { useQuoteBuilderApi } from '../api/client'
import {
  customerGreeting,
  quoteEmailsApi,
  quoteEmailsQueryKey,
  quoteEmailsQueryOptions,
  type QuoteAction,
  type QuoteEmails,
  type QuoteThread,
  invoiceEmailsQueryOptions,
  recordInvoiceReply,
} from './api'
import { ComposeInput, ComposeTextarea } from '@/components/compose-field'

/** A new conversation with the customer, laid out like the PO page's New Thread dialog. */
function NewQuoteEmailDialog({ quoteId, emails }: { quoteId: string; emails: QuoteEmails }) {
  const api = useQuoteBuilderApi()
  const client = useQueryClient()
  const [open, setOpen] = useState(false)
  const [subject, setSubject] = useState('')
  const [bodyText, setBodyText] = useState('')
  const start = useMutation({
    mutationFn: () => quoteEmailsApi.start(api, quoteId, subject.trim(), bodyText.trim()),
    onSuccess: (next) => {
      client.setQueryData(quoteEmailsQueryKey(quoteId), next)
      toast.success('Email sent')
      setOpen(false)
      setSubject('')
      setBodyText('')
    },
  })
  if (!emails.recipient_email) return null
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <MessageSquare className="mr-1 h-3 w-3" /> New Email
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>New email to the customer</DialogTitle>
            <DialogDescription>Sent from {emails.mailbox}. Replies come back to this conversation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">To</label>
              <ComposeInput readOnly value={emails.recipient_email} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground" htmlFor="quote-email-subject">
                Subject
              </label>
              <ComposeInput id="quote-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground" htmlFor="quote-email-body">
                Message
              </label>
              <ComposeTextarea id="quote-email-body" value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={8} />
            </div>
          </div>
          {start.isError && (
            <div
              role="alert"
              className="flex gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <p>{formatApiErrorMessage('Could not send the email', start.error)}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <LoadingButton
              loading={start.isPending}
              loadingText="Sending..."
              onClick={() => start.mutate()}
              disabled={!subject.trim() || !bodyText.trim()}
            >
              <MessageSquare className="mr-1 h-3 w-3" /> Send
            </LoadingButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/** The quote's open action items, in the PO thread's action-item style, with Done and Dismiss. */
function OpenActionItems({
  quoteId,
  actions,
  invoiceId,
}: {
  quoteId: string
  actions: QuoteAction[]
  invoiceId?: string
}) {
  const api = useQuoteBuilderApi()
  const client = useQueryClient()
  const update = useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'resolved' | 'dismissed' }) =>
      quoteEmailsApi.updateAction(api, quoteId, id, status),
    onSuccess: () => void client.invalidateQueries({ queryKey: quoteEmailsQueryKey(quoteId, invoiceId) }),
    onError: (error) => toast.error(formatApiErrorMessage('Could not update the action item', error)),
  })
  const open = actions.filter((action) => action.status === 'open')
  if (!open.length) return null
  return (
    <ul aria-label="Open action items" className="space-y-1.5">
      {open.map((action) => (
        <li
          className="flex items-start gap-2 rounded-md bg-amber-50 px-2.5 py-2 text-amber-950 dark:bg-amber-950/40 dark:text-amber-100"
          key={action.id}
        >
          <CircleAlert aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {actionTypeBadge(action.action_type)}
              {action.assigned_role ? (
                <span className="font-medium capitalize text-amber-700 dark:text-amber-300">
                  {action.assigned_role}
                </span>
              ) : null}
            </div>
            <p className="break-words text-sm text-foreground">{action.summary}</p>
            {action.details ? <p className="break-words text-xs text-muted-foreground">{action.details}</p> : null}
          </div>
          <div className="flex shrink-0 gap-1">
            <LoadingButton
              loading={update.isPending && update.variables?.id === action.id && update.variables.status === 'resolved'}
              onClick={() => update.mutate({ id: action.id, status: 'resolved' })}
              size="sm"
              variant="outline"
            >
              <Check className="mr-1 h-3 w-3" /> Done
            </LoadingButton>
            <Button
              aria-label={`Dismiss: ${action.summary}`}
              disabled={update.isPending && update.variables?.id === action.id}
              onClick={() => update.mutate({ id: action.id, status: 'dismissed' })}
              size="icon"
              variant="ghost"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </li>
      ))}
    </ul>
  )
}

/**
 * A quote's (or one invoice's) customer conversations, on the PO page's conversation components:
 * thread list, AI analysis, drafted replies to approve, recording a call, follow-ups.
 */
export function QuoteConversations({
  quoteId,
  invoiceId,
  title = 'Customer emails',
}: {
  /** Empty for an invoice cut with no quote (USA-378): its own conversations are read instead. */
  quoteId: string | null
  /** Only this invoice's conversations (the AR invoice page). */
  invoiceId?: string
  title?: string
}) {
  const api = useQuoteBuilderApi()
  const receivables = useReceivablesApi()
  const client = useQueryClient()
  const query = useQuery(
    quoteId
      ? quoteEmailsQueryOptions(api, quoteId, invoiceId)
      : invoiceEmailsQueryOptions(receivables, invoiceId ?? ''),
  )
  const approve = useApproveMessage()
  const reject = useRejectMessage()
  const send = useSendMessage()
  const update = useUpdateMessage()
  const record = useMutation({
    mutationFn: ({ thread, body, source }: { thread: QuoteThread; body: string; source: string }) => {
      const recorded = source === 'in_person' || source === 'other' ? source : 'phone_call'
      return quoteId
        ? quoteEmailsApi.record(api, quoteId, thread.uuid, body, recorded)
        : recordInvoiceReply(receivables, invoiceId ?? '', thread.uuid, body, recorded)
    },
    onSuccess: () => {
      toast.success('Communication recorded')
      void client.invalidateQueries({ queryKey: quoteEmailsQueryKey(quoteId ?? 'none', invoiceId) })
    },
    onError: (error) => toast.error(formatApiErrorMessage('Could not record it', error)),
  })
  const emails = query.data

  return (
    <div className="space-y-3">
      {emails && quoteId ? <OpenActionItems actions={emails.actions} invoiceId={invoiceId} quoteId={quoteId} /> : null}
      <POConversations
        actions={emails?.actions ?? []}
        contextForThread={(thread) => ({
          badge:
            thread.qb_invoice_id && !invoiceId ? (
              <Link
                className="inline-flex shrink-0 items-center gap-1 rounded-md border bg-muted/40 px-1.5 py-0.5 text-[11px] font-medium text-primary hover:underline"
                onClick={(event) => event.stopPropagation()}
                params={{ invoiceId: thread.qb_invoice_id }}
                to="/receivables/$invoiceId"
              >
                <Receipt aria-hidden className="h-3 w-3" />
                Invoice
              </Link>
            ) : thread.notification_type === 'quote_request' ? (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-md border bg-muted/40 px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                <Inbox aria-hidden className="h-3 w-3" />
                Quote request
              </span>
            ) : undefined,
          greeting: customerGreeting(thread),
          signoff: `Thanks,\n${thread.sender_name ?? emails?.sender_name ?? ''}`.trim(),
          // Already signed by the rep whose mailbox sends it.
          personalSignoff: false,
          poDocuments: false,
          onRecord: (body, source) => record.mutate({ thread, body, source }),
          isRecording: record.isPending,
          recordPlaceholder: 'Summarize what the customer said...',
        })}
        emptyState={
          <Card>
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              {emails?.recipient_email ? 'No emails yet' : 'Add the customer’s email on the Customer step.'}
            </CardContent>
          </Card>
        }
        hasPoPdf={false}
        hasQuotePdf={false}
        isLoading={query.isLoading}
        newThreadAction={emails && quoteId && !invoiceId ? <NewQuoteEmailDialog emails={emails} quoteId={quoteId} /> : null}
        onApprove={(uuid) => approve.mutate(uuid)}
        onReject={(uuid) => reject.mutate(uuid)}
        onSend={(uuid) => send.mutate(uuid)}
        onUpdate={(uuid, body) => update.mutate({ messageUuid: uuid, body })}
        shipmentDocOptions={[]}
        threads={emails?.threads}
        title={title}
      />
    </div>
  )
}
